// src/pages/Signup.jsx
// Registration screen creating a new tenant (workspace) for this user.
// On success → stores token → redirects to /setup.

import React, { useState } from "react";
import { apiPost, setToken } from "../lib/api";

export default function Signup() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    companyName: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);
    setLoading(true);

    try {
      // Backend /auth/register deve:
      // - criar Tenant usando companyName
      // - criar User com tenantId e role = 'owner' (ou 'admin')
      // - retornar { token, user: { ..., tenantId, role } }
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        companyName: form.companyName.trim(),
      };

      const data = await apiPost("/auth/register", payload);

      if (data?.token) {
        setToken(data.token);
        if (data.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
        }
        setSuccess(true);
        // Primeira parada: setup do tenant (profile, informative panel, phone numbers etc.)
        window.location.href = "/setup";
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Registration failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gray-50 p-6">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow p-6">
        <h1 className="text-2xl font-semibold mb-1">Create your workspace</h1>
        <p className="text-sm text-gray-500 mb-6">
          Sign up to create your own tenant and start using the messaging
          assistant.
        </p>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 mb-4">
            {error}
          </div>
        )}
        {success && (
          <div className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-md p-3 mb-4">
            Account created! Redirecting...
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-1">Full name</label>
            <input
              name="name"
              value={form.name}
              onChange={onChange}
              required
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Work e-mail</label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              required
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Company / workspace name</label>
            <input
              name="companyName"
              value={form.companyName}
              onChange={onChange}
              required
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring"
              placeholder="Sun Beach Rentals, My Law Firm..."
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Password</label>
            <input
              name="password"
              type="password"
              value={form.password}
              onChange={onChange}
              required
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl py-2.5 font-medium bg-black text-white hover:opacity-90 disabled:opacity-60"
          >
            {loading ? "Creating..." : "Sign up"}
          </button>
        </form>

        <p className="text-xs text-gray-400 mt-4">
          Already have an account?{" "}
          <a href="/login" className="underline">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}
